//#region types
import { WintUtilsWebApi } from "wint-api-client/dist/types";
import { StyleEnums } from "models/enums";
import { ListAPIResponse, TableRowCommonKeys, TableRowProperties } from "models/propTypes/simpleTableProps";
import { DateConstants } from "utils/constants";
import { TranslationKeys } from "translations";
import { Amount } from "models/utilTypes/currencyType";
//#endregion
//#region components
import { SnackbarKey } from "notistack";
import useValueFormatter from "./useValueFormatter";
import useFloatingMessages from "../messages/useFloatingMessages";
import useDownload from "../useDownload";
import TableAsyncPrint from "./print/tableAsyncPrint";
//#endregion
import ExportLoadingMessage from "components/common/messages/exportLoadingMessage";
import { ExportHelper, LocalizationHelperInstance, ServiceHelper, TableHelper, UtilsHelper } from "utils/helpers";

function useAsyncTableExport<RowType extends Record<string, any>>(
  columns: [string, Record<keyof TableRowCommonKeys | keyof RowType, TableRowProperties>[string] | undefined][],
  filter:
    | (<T extends WintUtilsWebApi.Filter>(
        filter: T,
        key: number,
        skipUndefinedConversion?: boolean
      ) => ListAPIResponse<RowType>)
    | RowType[],
  getApiFilter: (page: number, overrideRowsPerPage?: number) => WintUtilsWebApi.Filter,
  orderByDescending: boolean,
  orderByProperty: string,
  disableSort: boolean | undefined,
  fileName?: string,
  exportFileDescription?: string,
  pdfHeaderTextSmall?: boolean,
  includeSummary?: boolean,
  summaryRow?: RowType,
  customSummaryInfoForExport?: string | ((apiResultItem: any) => string | undefined),
  summaryColumns?: [string, Record<keyof TableRowCommonKeys | keyof RowType, TableRowProperties>[string] | undefined][],
  total?: number,
  totalWithoutFilter?: number
) {
  const { getFormattedValueForPrint, getSumamryFormattedValueForPrint } = useValueFormatter();
  const { enqueueMessage, closeMessage } = useFloatingMessages();

  const { handleDownloadPDFRender } = useDownload();

  const loadingMessage = <ExportLoadingMessage />;

  const getAllPages = async (skipUndefinedConversion?: boolean) => {
    if (typeof filter === "function") {
      const getPage = (page: number) => {
        const apiFilter: WintUtilsWebApi.Filter = getApiFilter(page, 100);
        return filter(apiFilter, 0, skipUndefinedConversion);
      };

      return ServiceHelper.getAllPages(getPage);
    } else {
      const allItems: RowType[] = disableSort
        ? filter
        : TableHelper.tableSortAsync(
            filter,
            TableHelper.getComparator(orderByDescending ? "desc" : "asc", orderByProperty as string)
          );

      return Promise.resolve(allItems);
    }
  };

  const exportAsPdf = (
    fileName: string,
    allRows: RowType[],
    reportDateTime: string,
    messageKey: SnackbarKey,
    title: string,
    subtitle?: string,
    pdfHeaderTextSmall?: boolean
  ) => {
    handleDownloadPDFRender(
      <TableAsyncPrint
        reportDateTime={reportDateTime}
        title={title}
        subTitle={subtitle}
        rows={allRows}
        columns={columns}
        pdfHeaderTextSmall={pdfHeaderTextSmall}
      />,
      fileName
    );
    closeMessage(messageKey);
  };

  const isACurrencyColumn = (
    [columnKey, value]: [
      string,
      Record<keyof TableRowCommonKeys | keyof RowType, TableRowProperties>[string] | undefined,
    ],
    firstRow?: RowType
  ) => {
    if (firstRow) {
      const columnValue = value?.formatter ? value.formatter(firstRow, 0) : firstRow[columnKey];
      return (
        UtilsHelper.notUndefinedOrNull(columnValue) && typeof columnValue === "object" && columnValue instanceof Amount
      );
    } else {
      return false;
    }
  };

  const getSummaryInfoForExport = () => {
    return customSummaryInfoForExport
      ? typeof customSummaryInfoForExport === "function"
        ? customSummaryInfoForExport(summaryRow)
        : customSummaryInfoForExport
      : LocalizationHelperInstance.getFormattedMessage(TranslationKeys.Common.SummaryShowsFilteredData, undefined, {
          currentTotal: `${total}`,
          totalWithoutFilter: `${totalWithoutFilter}`,
        });
  };

  const printSpreadsheet = (
    fileNameWithExtension: string,
    columnHeaders: (string | number)[],
    allRows: RowType[],
    messageKey: SnackbarKey,
    firstRow?: RowType
  ) => {
    const getColumnValues = (allRows: RowType[]) => {
      let printValues: (string | number)[][] = [];

      allRows.forEach((row, rowIndex) => {
        let rowValues: (string | number)[] = [];

        columns.forEach(([columnKey, cellValue], columnIndex) => {
          const valueToPrint = cellValue?.valueToPrint
            ? cellValue.valueToPrint(row, rowIndex)
            : getFormattedValueForPrint(cellValue?.formatter ? cellValue.formatter(row, rowIndex) : row[columnKey]);

          if (typeof valueToPrint === "string") {
            rowValues = [...rowValues, valueToPrint];
          } else {
            rowValues = [...rowValues, ...valueToPrint];
          }
        });

        printValues = [...printValues, rowValues];
      });

      return printValues;
    };

    const getSummaryRow = () => {
      if (!includeSummary) return [];

      let summaryRowCellValues: string[] = [];

      const summaryInfoText = getSummaryInfoForExport();
      if (summaryInfoText !== undefined) {
        summaryRowCellValues[0] = summaryInfoText;
      }

      if (summaryColumns !== undefined && firstRow) {
        columns.forEach(([columnKey, value], columnIndex) => {
          if (columnIndex === 0) {
            return;
          }

          let summaryValueToPrint = "";

          const filteredSummaryColumns = summaryColumns.filter(([summaryColumnKey, cellValue], cellIndex) => {
            return summaryColumnKey === columnKey;
          });

          if (filteredSummaryColumns.length > 0) {
            const [, summaryColumnCellValue] = filteredSummaryColumns[0];

            const summaryValue = summaryColumnCellValue?.summaryToPrint
              ? summaryColumnCellValue.summaryToPrint(summaryRow)
              : summaryColumnCellValue?.summaryValueFormatter
                ? summaryColumnCellValue.summaryValueFormatter(summaryRow)
                : undefined;

            summaryValueToPrint = summaryValue !== undefined ? getSumamryFormattedValueForPrint(summaryValue) : "";
          }

          const isACurrency = isACurrencyColumn([columnKey, value], firstRow);
          if (isACurrency) {
            summaryRowCellValues = [...summaryRowCellValues, "", summaryValueToPrint];
          } else {
            summaryRowCellValues = [...summaryRowCellValues, summaryValueToPrint];
          }
        });
      }

      return summaryRowCellValues;
    };

    const toPrint = getColumnValues(allRows);
    const summary = getSummaryRow();

    ExportHelper.exportReport([columnHeaders].concat(toPrint).concat([summary]), fileNameWithExtension, undefined);
    closeMessage(messageKey);
  };

  const exportReport = (fileExtension: "xlsx" | "csv" | "pdf") => {
    fileName = fileName ?? "report";
    const reportDateTime = LocalizationHelperInstance.getFormattedDate(new Date(), DateConstants.DateTimeConfig);
    const fileNameFormatted = `${fileName}_${reportDateTime}`;
    const fileNameWithExtension = `${fileNameFormatted}.${fileExtension}`;

    const messageKey = enqueueMessage(loadingMessage, StyleEnums.DisplayType.Info);

    const currencyText = LocalizationHelperInstance.getFormattedMessage(TranslationKeys.Common.Currency);
    const valueText = LocalizationHelperInstance.getFormattedMessage(TranslationKeys.Common.Value);

    const getColumnHeaders = (firstRow?: RowType) => {
      let headerStrings: string[] = [];

      columns.forEach(([headerKey, value], columnIndex) => {
        let isACurrency = false;
        const header =
          value?.descriptionToPrint ??
          (value?.description && typeof value?.description == "string"
            ? LocalizationHelperInstance.getFormattedMessage(value.description)
            : "");

        if (firstRow) {
          const columnValue = value?.formatter ? value.formatter(firstRow, 0) : firstRow[headerKey];
          isACurrency =
            UtilsHelper.notUndefinedOrNull(columnValue) &&
            typeof columnValue === "object" &&
            columnValue instanceof Amount;
        }

        if (isACurrency) {
          headerStrings = [...headerStrings, header.concat(`: ${currencyText}`), header.concat(`: ${valueText}`)];
        } else {
          headerStrings = [...headerStrings, header];
        }
      });

      return headerStrings;
    };

    getAllPages().then((allPages) => {
      if (fileExtension === "pdf") {
        exportAsPdf(
          fileNameWithExtension,
          allPages,
          reportDateTime,
          messageKey,
          `${fileName}`,
          exportFileDescription,
          pdfHeaderTextSmall
        );
      } else {
        const firstRow = allPages.length > 0 ? allPages[0] : undefined;
        const columnHeaders = getColumnHeaders(firstRow);
        printSpreadsheet(fileNameWithExtension, columnHeaders, allPages, messageKey, firstRow);
      }
    });
  };

  const getFullAPIResult = () => getAllPages(true);

  const exportRawData = (fileExtension: "xlsx" | "csv") => {
    const messageKey = enqueueMessage(loadingMessage, StyleEnums.DisplayType.Info);

    const printRawData = (rows: Record<string, any>[]) => {
      let toPrintContent: string[][] = [];

      let toPrintKeys: string[] = [];
      let baseObjKeys: string[] = [];
      if (rows.length > 0) {
        baseObjKeys = Object.keys(rows[0]);
        toPrintKeys = [...baseObjKeys];

        toPrintContent = rows
          .map((row) => {
            const rowToPrint: Record<string, string> = {};

            baseObjKeys.forEach((baseObjKey) => {
              const propertyValue: any = row[baseObjKey] ?? "";
              if (Array.isArray(propertyValue)) {
                const propertyIndex = toPrintKeys.findIndex((key) => key === baseObjKey);
                if (propertyIndex >= 0) {
                  toPrintKeys.splice(propertyIndex, 1);
                }

                if (propertyValue.length > 0) {
                  const firstRowValue = propertyValue[0] ?? "";
                  if (typeof firstRowValue === "object") {
                    const internalArrayObjKeys = Object.keys(propertyValue[0]);
                    propertyValue.forEach((internalRow: any, rowIndex) => {
                      internalArrayObjKeys.forEach((internalArrayObjKey) => {
                        const formattedCellKey = `${baseObjKey}${rowIndex}: ${internalArrayObjKey}`;
                        if (toPrintKeys.findIndex((key) => key === formattedCellKey) < 0) {
                          toPrintKeys.push(formattedCellKey);
                        }
                        rowToPrint[formattedCellKey] = (internalRow[internalArrayObjKey] ?? "").toString();
                      });
                    });
                  } else {
                    rowToPrint[baseObjKey] = propertyValue.join(", ");
                  }
                }
              } else if (typeof propertyValue === "object" && Object.keys(propertyValue).length > 0) {
                const propertyIndex = toPrintKeys.findIndex((key) => key === baseObjKey);
                if (propertyIndex >= 0) {
                  toPrintKeys.splice(propertyIndex, 1);
                }

                const internalObjKeys = Object.keys(propertyValue);
                internalObjKeys.forEach((internalObjKey) => {
                  const formattedCellKey = `${baseObjKey}: ${internalObjKey}`;
                  if (toPrintKeys.findIndex((key) => key === formattedCellKey) < 0) {
                    toPrintKeys.push(formattedCellKey);
                  }
                  rowToPrint[formattedCellKey] = (propertyValue[internalObjKey] ?? "").toString();
                });
              } else {
                rowToPrint[baseObjKey] = (row[baseObjKey] ?? "").toString();
              }
            });

            return rowToPrint;
          })
          .map((row) => {
            return toPrintKeys.map((key) => row[key] ?? "");
          });

        ExportHelper.exportReport(
          [toPrintKeys].concat(toPrintContent),
          `${fileName}_${LocalizationHelperInstance.getFormattedDate(
            new Date(),
            DateConstants.DateTimeConfig
          )}.${fileExtension}`,
          undefined
        );

        closeMessage(messageKey);
      }
    };

    getFullAPIResult().then((rows) => printRawData(rows));
  };

  return { exportReport, exportRawData, getFullAPIResult };
}

export default useAsyncTableExport;
